import dayjs from "dayjs";
import * as XLSX from "xlsx";

import type { SupplierDocumentAtCutoff } from "~/types/purchasing";

// The two "análisis de vencimiento de proveedores" reports the client provided. Both share the same
// layout (Saldo + the asiento glosa as the final column); Reporte 1 is line-by-line, Reporte 2 adds
// a subtotal row per proveedor.
const HEADERS = [
  "Codigo",
  "Nombre",
  "Cuenta",
  "Documento Tipo",
  "Nº",
  "Fecha Emision",
  "Fecha Vcto.",
  "Tipo de cambio",
  "Saldo",
  "Glosa",
];

const COL_WIDTHS = [
  { wch: 12 }, // Codigo
  { wch: 44 }, // Nombre
  { wch: 10 }, // Cuenta
  { wch: 14 }, // Documento Tipo
  { wch: 12 }, // Nº
  { wch: 14 }, // Fecha Emision
  { wch: 14 }, // Fecha Vcto.
  { wch: 14 }, // Tipo de cambio
  { wch: 18 }, // Saldo
  { wch: 60 }, // Glosa
];

const fmtDate = (d: string | null) => (d ? dayjs(d).format("DD/MM/YYYY") : "");

type Cell = string | number;

// One document → a 10-cell row (Saldo kept numeric so Excel treats it as a number; the asiento
// glosa is the final column).
function docRow(d: SupplierDocumentAtCutoff): Cell[] {
  return [
    d.softland_code ?? "",
    d.nombre,
    d.cuenta ?? "",
    d.doc_tipo_code ?? "",
    d.folio ?? "",
    fmtDate(d.emission_date),
    fmtDate(d.due_date),
    d.exchange_rate ?? "",
    Number(d.saldo_at),
    d.glosa ?? "",
  ];
}

function writeReport(aoa: Cell[][], sheetName: string, fileName: string) {
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws["!cols"] = COL_WIDTHS;
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, fileName);
}

/** Reporte 1 — Consolidado (nacional / exterior): one row per open document + grand total. */
export function exportSupplierAgingConsolidado(
  docs: SupplierDocumentAtCutoff[],
  cutoff: string
) {
  const aoa: Cell[][] = [HEADERS];
  let total = 0;
  for (const d of docs) {
    aoa.push(docRow(d));
    total += Number(d.saldo_at);
  }
  aoa.push(["", "", "", "", "", "", "", "", total, ""]);
  writeReport(
    aoa,
    "Total",
    `Analisis_Vencimiento_Proveedores_Consolidado_${cutoff}.xlsx`
  );
}

/** Reporte 2 — Por código: documents grouped by proveedor with a subtotal row + a final total. */
export function exportSupplierAgingPorCodigo(
  docs: SupplierDocumentAtCutoff[],
  cutoff: string
) {
  const aoa: Cell[][] = [HEADERS];
  let grandTotal = 0;
  let i = 0;
  while (i < docs.length) {
    const supplierId = docs[i].supplier_id;
    const nombre = docs[i].nombre;
    let subtotal = 0;
    while (i < docs.length && docs[i].supplier_id === supplierId) {
      aoa.push(docRow(docs[i]));
      subtotal += Number(docs[i].saldo_at);
      i += 1;
    }
    aoa.push(["", `subtotal ${nombre}`, "", "", "", "", "", "", subtotal, ""]);
    grandTotal += subtotal;
  }
  aoa.push(["", "Total Proveedores", "", "", "", "", "", "", grandTotal, ""]);
  writeReport(
    aoa,
    "Desglose por Código",
    `Analisis_Vencimiento_Proveedores_Por_Codigo_${cutoff}.xlsx`
  );
}
